import type { ProviderId } from '../providers/types';
import type { GameMapId } from './maps';
import { MAX_ROUND_SCORE } from './scoring';
import { readJson, removeKey, writeJson } from './storage';

export interface RoundRecord {
  score: number;
  /** null when the time ran out before a guess. */
  distanceKm: number | null;
}

export interface GameRecord {
  id: string;
  date: string;
  mapId: GameMapId;
  providerId: ProviderId;
  score: number;
  timeLimit: number;
  allowMove: boolean;
  rounds: RoundRecord[];
}

export interface PlayerStats {
  version: 1;
  gamesPlayed: number;
  roundsPlayed: number;
  totalScore: number;
  perfectRounds: number;
  closestGuessKm: number | null;
  bestGame: GameRecord | null;
  bestByMap: Partial<Record<GameMapId, number>>;
  /** Most recent games first. */
  history: GameRecord[];
}

export const HISTORY_LENGTH = 15;
const KEY = 'geogessr:stats:v1';

export function emptyStats(): PlayerStats {
  return {
    version: 1,
    gamesPlayed: 0,
    roundsPlayed: 0,
    totalScore: 0,
    perfectRounds: 0,
    closestGuessKm: null,
    bestGame: null,
    bestByMap: {},
    history: [],
  };
}

export interface RecordOutcome {
  stats: PlayerStats;
  isBestOverall: boolean;
  isBestOnMap: boolean;
}

/** Adds a finished game to the statistics (pure function). */
export function recordGame(stats: PlayerStats, game: GameRecord): RecordOutcome {
  const previousBestOnMap = stats.bestByMap[game.mapId];
  const isBestOnMap = previousBestOnMap === undefined || game.score > previousBestOnMap;
  const isBestOverall = stats.bestGame === null || game.score > stats.bestGame.score;
  const distances = game.rounds.map((r) => r.distanceKm).filter((d): d is number => d !== null);
  const closest = distances.length > 0 ? Math.min(...distances) : null;

  return {
    isBestOnMap,
    isBestOverall,
    stats: {
      ...stats,
      gamesPlayed: stats.gamesPlayed + 1,
      roundsPlayed: stats.roundsPlayed + game.rounds.length,
      totalScore: stats.totalScore + game.score,
      perfectRounds: stats.perfectRounds + game.rounds.filter((r) => r.score >= MAX_ROUND_SCORE).length,
      closestGuessKm:
        closest === null ? stats.closestGuessKm : stats.closestGuessKm === null ? closest : Math.min(stats.closestGuessKm, closest),
      bestGame: isBestOverall ? game : stats.bestGame,
      bestByMap: isBestOnMap ? { ...stats.bestByMap, [game.mapId]: game.score } : stats.bestByMap,
      history: [game, ...stats.history].slice(0, HISTORY_LENGTH),
    },
  };
}

export function averageScore(stats: PlayerStats): number {
  return stats.gamesPlayed > 0 ? Math.round(stats.totalScore / stats.gamesPlayed) : 0;
}

function isPlayerStats(value: unknown): value is PlayerStats {
  const v = value as PlayerStats;
  return (
    !!v &&
    v.version === 1 &&
    typeof v.gamesPlayed === 'number' &&
    typeof v.totalScore === 'number' &&
    Array.isArray(v.history) &&
    typeof v.bestByMap === 'object'
  );
}

export function loadStats(): PlayerStats {
  const stored = readJson<unknown>(KEY);
  return isPlayerStats(stored) ? { ...emptyStats(), ...stored } : emptyStats();
}

export function saveStats(stats: PlayerStats): boolean {
  return writeJson(KEY, stats);
}

export function clearStats(): void {
  removeKey(KEY);
}
